import { AccButton, AccButtonProps } from "@components/base/AccButton";
import React, { ReactNode } from "react";
import cn from "classnames";
import MDXLink from "@components/MDXLink";

export type ButtonColorProp =
  | "purple"
  | "orange"
  | "lightPurple"
  | "green"
  | "lightGray"
  | "outline-gray";

export type ButtonProps = AccButtonProps & {
  children: ReactNode;
  /** Main color of AccuRank */
  color?: ButtonColorProp;
  /** If the button is disabled */
  disabled?: boolean;
  /** If the text in button should always be in one line */
  wrap?: boolean;
  /** If "link" is provided, we apply an MDXLink */
  link?: string;
  borderRadius?: "medium" | "full";
  padding?: "large" | "standard" | "medium" | "small";
};

const Button = ({
  children,
  color,
  disabled = false,
  wrap = false,
  link,
  className,
  borderRadius = "medium",
  padding = "standard",
  ...props
}: ButtonProps) => {
  return (
    <AccButton
      className={cn(
        `acc-button font-medium text-white transition-colors duration-300`,
        {
          "rounded-md": borderRadius === "medium",
          "rounded-full": borderRadius !== "medium",
          "bg-accuRankerPurpleOpacity-dark hover:bg-accuRankerPurpleOpacity-light":
            color === "purple",
          "bg-accuRankerPurple-500 hover:bg-accuRankerPurple-400":
            color === "lightPurple",
          "bg-accuRankerOrange-500 hover:bg-accuRankerOrange-900":
            color === "orange",
          "bg-accuRankerGreen-500 hover:bg-accuRankerGreen-600":
            color === "green",
          "border border-white border-opacity-20 text-white hover:border-opacity-10":
            color === "outline-gray",
          "bg-white bg-opacity-20 hover:bg-opacity-10": color === "lightGray",
          "cursor-not-allowed opacity-40": disabled,
          "whitespace-nowrap": wrap,
          "min-h-[48px] px-8 py-4": padding === "large",
          "min-h-[48px] px-4 py-2": padding === "standard",
          "px-4 py-2": padding === "medium",
          "min-h-[38px] px-1 py-1": padding === "small",
        },
        className
      )}
      disabled={disabled}
      {...props}
    >
      {link ? <MDXLink href={link}>{children}</MDXLink> : <>{children}</>}
    </AccButton>
  );
};

export default Button;
